import { connect } from 'react-redux'
import {
  getContact,
  getStorageFile,
  restoreStorageFiles,
  setRoleLocally,
  getRole,
  saveRole,
  updateMediaPlayerContact,
  getStorageFileRequested,
  updateReviewGoal,
  saveArchiveAudit,
  saveAudioPlayedAudit,
  getTenantLicensedFeatures,
} from './mediaPlayer.actions'
import { updateContactSuccess } from '../recordings.actions'
import { getSpeech, getSpeechSuccess } from '../SpeechPanel/speech.actions'
import { getEvents } from '../AudioPanel/events.actions'
import { DESKTOP_PROPERTIES } from '../../../Common/DesktopProperties/desktopProperties.constants'
import { getDesktopProperties } from '../../../Common/DesktopProperties/desktopProperties.actions'

import MediaPlayer from './MediaPlayer'
import {
  getMediaTimeline,
  getNextAndPreviousContactIds,
  getDelayedStorageStatus,
  makeGetTimezoneOverride,
  getHasReviewGoal,
} from './mediaPlayer.selectors'
import { withAuthenticatedUser, PERMISSIONS } from '../../../Permissions'
import { CONTACT_TYPE, ROLE_ANALYST, ROLE_EVALUATOR } from './mediaPlayer.constants' // roles may be used in the future
import { ACTIONS as GOAL_ACTIONS } from '../CreateTask/createTask.constants'

const chooseRoleBasedOnPermissions = hasAnyPermission =>
  hasAnyPermission([
    PERMISSIONS.VIEW_AUDIO_ANALYTICS,
    PERMISSIONS.VIEW_LVCSR_ANALYTICS,
    PERMISSIONS.VIEW_DESKTOP_ANALYTICS,
  ])
    ? ROLE_ANALYST
    : ROLE_EVALUATOR

const makeMapStateToProps = () => (state, props) => {
  const {
    authenticatedUser: { hasAnyPermission },
  } = props
  const media = state.media
  const { main } = media
  const { dxStatus } = state.app
  return {
    hasDx: dxStatus,
    media,
    timezoneOverride: makeGetTimezoneOverride()(state, props),
    mediaTimeline: getMediaTimeline(main),
    nextAndPreviousContactIds: getNextAndPreviousContactIds(media),
    role: main.role.data && main.role.data.role ? main.role.data.role : chooseRoleBasedOnPermissions(hasAnyPermission),
    delayedStorageStatus: getDelayedStorageStatus(state),
    mediaFormats: state.app.mediaFormats,
    hasReviewGoal: getHasReviewGoal(main),
  }
}

const mapDispatchToProps = (dispatch, { authenticatedUser: { hasPermission, hasAnyPermission } }) => ({
  async getContact(ccrid, { scopeType, goalId }) {
    const contact = await dispatch(getContact(ccrid, { scopeType, goalId }))
    return contact
  },
  async resetStorageFile() {
    dispatch(getStorageFileRequested())
  },
  async getStorageFile(ccrid, isRootRecording) {
    await dispatch(getStorageFile(ccrid, isRootRecording))
  },
  async restoreStorageFiles(ccrId, mediaFormats, isRootRecording) {
    await dispatch(restoreStorageFiles(ccrId, mediaFormats, isRootRecording))
  },
  async getSpeech(ccrid, canViewAudioAnalytics, isRootRecording) {
    await dispatch(canViewAudioAnalytics ? getSpeech(ccrid, { isRootRecording }) : getSpeechSuccess({}))
  },
  async getEvents(ccrid) {
    await dispatch(getEvents(ccrid))
  },
  async getMediaPlayerRole() {
    try {
      await dispatch(getRole())
    } catch (error) {
      dispatch(setRoleLocally(chooseRoleBasedOnPermissions(hasAnyPermission)))
    }
  },
  async saveMediaPlayerRole(role) {
    try {
      await dispatch(saveRole(JSON.stringify({ role })))
    } catch (error) {
      dispatch(setRoleLocally(role))
    }
  },
  async updateContact(contact) {
    return await dispatch(updateMediaPlayerContact(contact))
  },
  async useUserTimezone(tenantId) {
    await dispatch(getDesktopProperties(DESKTOP_PROPERTIES.WILL_DISPLAY_CONTACT_IN_USER_TIMEZONE, tenantId))
  },
  async completeReviewGoal(contactId, goalId, userId) {
    await dispatch(updateReviewGoal(contactId, goalId, { complete: true, personId: userId }))
  },
  async saveArchiveAudit(contactId, isRootRecording, timezone) {
    await dispatch(saveArchiveAudit(contactId, isRootRecording, timezone))
  },
  async saveAudioPlayedAudit(contactId, isRootRecording) {
    if (hasPermission(PERMISSIONS.VIEW_AUDIO_ANALYTICS)) {
      await dispatch(saveAudioPlayedAudit(contactId, isRootRecording))
    }
  },
  async onMediaPlayerClose(contact) {
    dispatch(updateContactSuccess(contact))
  },
  async getTenantLicensedFeatures() {
    await dispatch(getTenantLicensedFeatures())
  },
})

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  async getContact(ccrid, scopeType) {
    const {
      authenticatedUser: { hasPermission, tenantId, user },
      goalId,
    } = ownProps
    const canViewAudioAnalytics = hasPermission(PERMISSIONS.VIEW_AUDIO_ANALYTICS)

    const tenantLicensedFeatures = dispatchProps.getTenantLicensedFeatures()
    const userTimezone = dispatchProps.useUserTimezone(tenantId)
    const role = dispatchProps.getMediaPlayerRole()

    dispatchProps.resetStorageFile()

    const contact = await dispatchProps.getContact(ccrid, { scopeType, goalId })
    if (!contact) {
      return
    }
    dispatchProps.saveArchiveAudit(ccrid, !!contact.isRootRecording, user.timezoneString)

    // If this is a non-call contact with a review goal then immediately complete it
    if (
      contact.goal !== null &&
      contact.goal.action === GOAL_ACTIONS.REVIEW &&
      contact.contactType === CONTACT_TYPE.NON_CALL
    ) {
      dispatchProps.completeReviewGoal(contact.id, goalId, { complete: true, personId: user.id })
    }

    const storageFile = dispatchProps.getStorageFile(ccrid, !!contact.isRootRecording)
    const speech = dispatchProps.getSpeech(ccrid, canViewAudioAnalytics, !!contact.isRootRecording)
    const events = dispatchProps.getEvents(ccrid)
    await tenantLicensedFeatures
    await userTimezone
    await role
    await storageFile
    await speech
    await events
  },
  async restoreStorageFiles() {
    await dispatchProps.restoreStorageFiles(ownProps.ccrid, stateProps.mediaFormats)
  },
})

export default withAuthenticatedUser(connect(makeMapStateToProps, mapDispatchToProps, mergeProps)(MediaPlayer))
